import { arrow, computePosition, flip, offset, shift } from "@floating-ui/dom";

// Mutates elements: arrow, tip
function update({ anchor, arrow: a, tip }, options) {
  computePosition(anchor, tip, options).then(({ x, y, placement, middlewareData }) => {
    Object.assign(tip.style, {
      left: `${x}px`,
      top: `${y}px`,
    });

    if (a) {
      const { x: arrowX, y: arrowY } = middlewareData.arrow;

      // See https://floating-ui.com/docs/tutorial#arrow-middleware for rationale.
      const staticSide = {
        top: "bottom",
        right: "left",
        bottom: "top",
        left: "right",
      }[placement.split("-")[0]];

      Object.assign(a.style, {
        left: arrowX == null ? "" : `${arrowX}px`,
        top: arrowY == null ? "" : `${arrowY}px`,
        [staticSide]: "-4px",
      });
    }
  });
}

// default offset accounts for arrow...
// arrow is 8px, so that we have no gap between anchor and arrow, when placement is left or right.
// see https://floating-ui.com/docs/offset.
const defaults = {
  delay: "100",
  flip: "true",
  offset: "8",
  padding: "6",
  placement: "right",
};

function parse(dataset) {
  const { delay, flip, offset, padding, placement } = Object.assign({}, defaults, dataset);
  return {
    delay: Number.parseInt(delay),
    flip: flip === "true",
    offset: Number.parseInt(offset),
    padding: Number.parseInt(padding),
    placement,
  };
}

function build({ delay, flip: f, offset: o, padding, placement }, arrowElement) {
  // ordering matters
  const middleware = [];
  if (o) middleware.push(offset(o));
  if (f) middleware.push(flip(f));
  if (padding) middleware.push(shift({ padding }));
  if (arrowElement) middleware.push(arrow({ element: arrowElement, padding }));
  return { delay, middleware, placement };
}

// event callbacks, as higher order functions
const hide =
  (i, state, elements, { delay }) =>
  () => {
    state[i] = false;

    // Here we may opt of setTimeout when delay is falsey (0, NaN)...
    setTimeout(() => {
      if (state.every((show) => show === false)) elements.tip.classList.add("hidden");
    }, delay);
  };

const show = (i, state, elements, options) => () => {
  if (!state[i]) {
    state[i] = true;
    elements.tip.classList.remove("hidden");
    update(elements, options);
  }
};

export const TooltipHook = {
  mounted() {
    // elements
    const anchor = this.el.querySelector(":nth-child(1)");
    const tip = this.el.querySelector('[role="tooltip"]');
    const arrow = tip.querySelector(":last-child");
    const elements = { anchor, arrow, tip };

    const options = build(parse(this.el.dataset), arrow);
    const state = [false, false];

    for (const [i, e] of [anchor, tip].entries()) {
      const showListener = show(i, state, elements, options);
      const hideListener = hide(i, state, elements, options);

      const events = [
        ["mouseenter", showListener],
        ["mouseleave", hideListener],
        ["focus", showListener],
        ["blur", hideListener],
      ];

      for (const [event, listener] of events) {
        e.addEventListener(event, listener);
      }
    }
  },
};
